import { Fragment, useCallback, useRef, useState } from 'react';
import type { MainSheetType, PenType, SheetType, StrokesBlock } from '../types/models';
import { SHEET_TYPE_LABELS } from '../types/models';
import { emptyStrokesPage } from '../utils/answers';
import { SHAPE_TOOLS, type ShapeKind } from '../utils/shapes';
import { InkCanvas } from './InkCanvas';

/** An extra sheet the student may pull in. `remaining` undefined = unlimited (no cap shown). */
export interface SheetBudgetItem {
  type: SheetType;
  remaining?: number;
}

interface Props {
  pages: StrokesBlock[];
  readOnly?: boolean;
  onChange: (next: StrokesBlock[]) => void;
  // The main answer-sheet style (chosen by staff at exam creation). New main pages use it.
  mainSheet?: MainSheetType;
  // Extra sheets the student may add (graph, engineering, log…), with their remaining counts.
  sheetBudget?: SheetBudgetItem[];
  // The question this answer belongs to — stamped onto each new page as its Q.No.
  questionNo?: string;
}

const COLORS = ['#111827', '#dc2626', '#2563eb', '#16a34a', '#ca8a04'];
const WIDTHS = [1.5, 2.5, 4, 6];
const PEN_TYPES: { value: PenType; label: string }[] = [
  { value: 'ballpoint', label: 'Ballpoint' },
  { value: 'gel', label: 'Gel pen' },
  { value: 'fountain', label: 'Fountain' },
  { value: 'marker', label: 'Marker' },
];
const ERASER_MIN = 2;
const ERASER_MAX = 120;
const ERASER_DEFAULT = 12;

// Session-wide palm-rejection default: ON, so by default only the stylus writes — a resting
// palm/finger never marks the sheet, and a finger scrolls instead. A finger-only user can turn
// it off from the toolbar; that choice then carries to other ink areas opened afterwards.
let sharedPenOnly = true;

/** A multi-page ink document. Owns the shared pen toolbar (colour / width / eraser / undo /
 *  clear) and the page list; each page is an A4-sized InkCanvas. Pages can be added and
 *  deleted — deleting a page that contains writing asks for confirmation first. */
export function InkPad({ pages, readOnly, onChange, mainSheet = 'ruled', sheetBudget = [], questionNo }: Props) {
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(WIDTHS[1]);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  const [penType, setPenType] = useState<PenType>('ballpoint');
  // Active shape tool (null = freehand). When set, a pen drag draws that measured shape.
  const [shape, setShape] = useState<ShapeKind | null>(null);
  // Whether the shapes dropdown is open, and whether the whole toolbar is expanded.
  const [shapesOpen, setShapesOpen] = useState(false);
  const [toolbarOpen, setToolbarOpen] = useState(true);
  const [eraserSize, setEraserSize] = useState(ERASER_DEFAULT);
  // Palm rejection: ignore finger/touch so only the stylus draws. ON by default so a resting hand
  // never marks the sheet (a finger scrolls instead); a finger-only user can turn it off, and that
  // choice sticks — we don't auto-flip it back when a stylus is seen.
  const [penOnly, setPenOnly] = useState(sharedPenOnly);
  const setPalm = (v: boolean) => { sharedPenOnly = v; setPenOnly(v); };
  // In-app confirm. We must NOT use window.confirm() here: native dialogs force the browser
  // out of fullscreen, which trips the exam's lockdown warning. This modal lives inside the
  // fullscreen element so the exam stays put.
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; action: () => void } | null>(null);

  // Keep the live pages and the parent sink in refs so a single stable per-page handler can
  // serve every InkCanvas. The handler matches the page by id carried on the updated block,
  // so InkCanvas instances for untouched pages keep an identical `onChange` prop and skip
  // re-rendering (see the memo on InkCanvas).
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const sinkRef = useRef(onChange);
  sinkRef.current = onChange;

  const onPageChange = useCallback((next: StrokesBlock) => {
    sinkRef.current(pagesRef.current.map(p => (p.id === next.id ? next : p)));
  }, []);

  const totalStrokes = pages.reduce((n, p) => n + p.strokes.length, 0);

  function addPage() {
    // A plain "more room" page in the exam's main sheet style.
    onChange([...pages, emptyStrokesPage(mainSheet, questionNo)]);
  }

  // Pull in one of the provisioned extra sheets (graph, engineering, log…) at the end.
  function addSheet(type: SheetType) {
    onChange([...pages, emptyStrokesPage(type, questionNo)]);
  }

  // Insert a sheet at a specific gap (0 = before page 1) so a student can drop a graph sheet
  // wherever they need it — start, in between, or end. `null` = no gap menu open.
  const [insertAt, setInsertAt] = useState<number | null>(null);
  function insertSheet(gap: number, type: SheetType) {
    const next = [...pages];
    next.splice(gap, 0, emptyStrokesPage(type, questionNo));
    onChange(next);
    setInsertAt(null);
  }

  // A slim "insert a sheet here" control shown between pages — only when extra sheets are
  // provisioned. Tapping it reveals the available sheet types to drop in at this exact spot.
  const showInserters = !readOnly && sheetBudget.length > 0;
  const renderInserter = (gap: number) => {
    if (!showInserters) return null;
    if (insertAt === gap) {
      return (
        <div className="ink-inserter open">
          <span className="muted" style={{ fontSize: 12 }}>Insert sheet here:</span>
          {sheetBudget.map(b => (
            <button key={b.type} disabled={b.remaining !== undefined && b.remaining <= 0}
                    onClick={() => insertSheet(gap, b.type)}
                    title={`Insert a ${SHEET_TYPE_LABELS[b.type]} sheet here`}>
              {SHEET_TYPE_LABELS[b.type]}
              {b.remaining !== undefined && <span className="muted"> ({b.remaining})</span>}
            </button>
          ))}
          <button onClick={() => setInsertAt(null)}>Cancel</button>
        </div>
      );
    }
    return (
      <div className="ink-inserter">
        <button className="ink-inserter-btn" onClick={() => setInsertAt(gap)}>+ Insert sheet here</button>
      </div>
    );
  };

  function deletePage(id: string) {
    const target = pages.find(p => p.id === id);
    if (!target) return;
    const doDelete = () => {
      const next = pages.filter(p => p.id !== id);
      // Always keep at least one page to write on.
      onChange(next.length > 0 ? next : [emptyStrokesPage()]);
    };
    if (target.strokes.length > 0) {
      setPendingConfirm({ message: "This page contains writing. Delete it and lose this page's work?", action: doDelete });
    } else {
      doDelete();
    }
  }

  function undo() {
    // Remove the most recent stroke from the last page that has any.
    for (let i = pages.length - 1; i >= 0; i--) {
      if (pages[i].strokes.length > 0) {
        onChange(pages.map((p, idx) =>
          idx === i ? { ...p, strokes: p.strokes.slice(0, -1) } : p));
        return;
      }
    }
  }

  function clearAll() {
    if (totalStrokes === 0) return;
    setPendingConfirm({
      message: 'Clear all ink on every page?',
      action: () => onChange(pages.map(p => ({ ...p, strokes: [] }))),
    });
  }

  return (
    <div className={`ink-pad${readOnly ? ' ink-pad--readonly' : ''}`}>
      {!readOnly && (
        <div className="ink-toolbar-wrap">
          <button
            className="ink-toolbar-toggle"
            onClick={() => setToolbarOpen(o => !o)}
            title={toolbarOpen ? 'Hide the tools' : 'Show the tools'}
            aria-expanded={toolbarOpen}
          >{toolbarOpen ? '▾ Tools' : '▸ Tools'}</button>
          {toolbarOpen && (
          <div className="ink-toolbar">
          <span className="muted">Ink</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {COLORS.map(c => (
              <div
                key={c}
                className={`swatch ${color === c ? 'active' : ''}`}
                style={{ background: c }}
                // Picking a colour leaves the eraser but keeps the current pen.
                onClick={() => { setColor(c); if (tool === 'eraser') setTool('pen'); }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {WIDTHS.map(w => (
              <button
                key={w}
                onClick={() => { setWidth(w); setTool('pen'); }}
                style={{ padding: '4px 8px', background: width === w && tool === 'pen' ? 'var(--accent-soft-bg)' : undefined }}
              >{w}</button>
            ))}
          </div>
          <select
            value={penType}
            onChange={e => { setPenType(e.target.value as PenType); setTool('pen'); }}
            title="Pen type"
            style={{ width: 'auto', padding: '4px 8px',
                     outline: tool === 'pen' ? '2px solid var(--accent)' : undefined }}
          >
            {PEN_TYPES.map(p => <option key={p.value} value={p.value}>✎ {p.label}</option>)}
          </select>
          <button
            onClick={() => { setTool('pen'); setShape(null); }}
            title="Freehand pen"
            aria-pressed={tool === 'pen' && shape === null}
            style={{ padding: '4px 8px', background: tool === 'pen' && shape === null ? 'var(--accent-soft-bg)' : undefined }}
          >✎</button>
          <div className="ink-shape-dd">
            <button
              onClick={() => setShapesOpen(o => !o)}
              title="Shapes — line, arrow, rectangle, circle, ellipse, arc (shows size in cm as you drag)"
              aria-expanded={shapesOpen}
              style={{ padding: '4px 8px', background: shape ? 'var(--accent-soft-bg)' : undefined }}
            >{(SHAPE_TOOLS.find(s => s.kind === shape)?.icon) ?? '◇'} ▾</button>
            {shapesOpen && (
              <>
                <div className="ink-dd-backdrop" onClick={() => setShapesOpen(false)} />
                <div className="ink-dd-menu">
                  {SHAPE_TOOLS.map(s => (
                    <button
                      key={s.kind}
                      onClick={() => { setTool('pen'); setShape(s.kind); setShapesOpen(false); }}
                      aria-pressed={tool === 'pen' && shape === s.kind}
                      style={{ background: tool === 'pen' && shape === s.kind ? 'var(--accent-soft-bg)' : undefined }}
                    >{s.icon}  {s.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => setTool(t => t === 'eraser' ? 'pen' : 'eraser')}
            title={tool === 'eraser' ? 'Eraser on — tap to switch back to the pen' : 'Eraser'}
            aria-pressed={tool === 'eraser'}
            style={{ background: tool === 'eraser' ? 'var(--danger-soft-bg)' : undefined }}
          >Erase</button>
          {sheetBudget.length > 0 && (
            <div className="sheet-tray" title="Extra sheets you can add — click to add one at the end">
              <span className="muted" style={{ fontSize: 12 }}>Add sheet:</span>
              {sheetBudget.map(b => (
                <button
                  key={b.type}
                  onClick={() => addSheet(b.type)}
                  disabled={b.remaining !== undefined && b.remaining <= 0}
                  title={`Add a ${SHEET_TYPE_LABELS[b.type]} sheet`}
                  style={{ padding: '4px 8px' }}
                >+ {SHEET_TYPE_LABELS[b.type]}
                  {b.remaining !== undefined && <span className="muted"> ({b.remaining})</span>}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setPalm(!penOnly)}
            title={penOnly
              ? 'Palm rejection ON — only the stylus draws; rest your hand freely (a finger scrolls). Tap to also allow finger drawing.'
              : 'Finger drawing ON — finger/touch also draws. Tap to switch to stylus-only (palm rejection).'}
            aria-pressed={penOnly}
            style={{ background: penOnly ? 'var(--accent-soft-bg)' : undefined }}
          >✋ {penOnly ? 'Stylus only' : 'Finger draws'}</button>
          {tool === 'eraser' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="range"
                min={ERASER_MIN}
                max={ERASER_MAX}
                step={1}
                value={eraserSize}
                onChange={e => setEraserSize(Number(e.target.value))}
                style={{ width: 140 }}
                aria-label="Eraser size"
                title="Eraser size"
              />
              <span style={{ minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {eraserSize}px
              </span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={undo} disabled={totalStrokes === 0}>Undo</button>
          <button onClick={clearAll} disabled={totalStrokes === 0}>Clear</button>
          <span className="muted" style={{ marginLeft: 'auto' }}>{totalStrokes} strokes</span>
        </div>
          )}
        </div>
      )}

      <div className="ink-pages">
        {pages.map((p, i) => {
          // The only page is deletable only once it has writing — there's nothing to remove
          // from a single empty page, and the document must always keep one page.
          const canDelete = !readOnly && (pages.length > 1 || p.strokes.length > 0);
          const pageSheet = (p.sheetType ?? (p.ruled === false ? 'plain' : 'ruled')) as SheetType;
          return (
            <Fragment key={p.id}>
            {renderInserter(i)}
            <div className="ink-page">
              <div className="ink-page-header">
                <span>Page {i + 1} of {pages.length}</span>
                <span className="ink-sheet-badge" title="Sheet type">{SHEET_TYPE_LABELS[pageSheet]}</span>
                <div style={{ flex: 1 }} />
                {!readOnly && (
                  <button
                    className="ink-page-delete"
                    onClick={() => deletePage(p.id)}
                    disabled={!canDelete}
                    title={canDelete ? 'Delete this page' : 'Nothing to delete'}
                    aria-label={`Delete page ${i + 1}`}
                  >✕ Delete page</button>
                )}
              </div>
              <InkCanvas
                block={p}
                readOnly={readOnly}
                tool={tool}
                color={color}
                width={width}
                eraserSize={eraserSize}
                penType={penType}
                shape={shape}
                penOnly={penOnly}
                onChange={onPageChange}
              />
            </div>
            </Fragment>
          );
        })}

        {renderInserter(pages.length)}

        {!readOnly && (
          <button className="ink-add-page" onClick={addPage}>
            + Add {SHEET_TYPE_LABELS[mainSheet]} page
          </button>
        )}
      </div>

      {pendingConfirm && (
        <div className="rte-modal-backdrop" onMouseDown={() => setPendingConfirm(null)}>
          <div className="rte-modal" style={{ width: 'min(420px, 92vw)' }} onMouseDown={e => e.stopPropagation()}>
            <div style={{ marginBottom: 16 }}>{pendingConfirm.message}</div>
            <div className="rte-modal-actions">
              <button type="button" onClick={() => setPendingConfirm(null)}>Cancel</button>
              <button type="button" className="primary"
                      onClick={() => { pendingConfirm.action(); setPendingConfirm(null); }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
